// src/api/queries/role-delegations.js
/**
 * Хуки замещений. GET /role-delegations, POST / (upsert: фронт передаёт
 * start/end, бэк ждёт startDate/endDate), POST /:id/decide { approved,
 * reason }, POST /:id/revoke, POST /:id/force-activate.
 *
 * GET /role-delegations возвращает либо массив, либо постраничный
 * конверт { items, meta } — нормализуем через unwrapItems
 * (utils/dbShape.js) прямо в хуке. Escape hatch — свой select через options.
 *
 * Инвалидация ["me"] в decide/revoke/force-activate обязательна: при
 * активации/деактивации делегирования effectiveRoles текущего
 * пользователя меняются (на бэке /me считает их по role_delegations),
 * и без инвалидации UI не обновит видимость. В create статус pending —
 * effectiveRoles не меняются, ["me"] не инвалидируется.
 *
 * ["tasks"] инвалидируется в decide/revoke/force-activate:
 * TaskService.applyDelegation/revertDelegation массово обновляют
 * assignee_id/delegated_from (bulkUpdateAssignee).
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";
import { unwrapItems } from "../../utils/dbShape";

export const ROLE_DELEGATIONS_KEY = ["role-delegations"];

export function useRoleDelegationsQuery(options = {}) {
  return useQuery({
    queryKey: ROLE_DELEGATIONS_KEY,
    queryFn: () => api.get("/role-delegations"),
    select: unwrapItems,
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useCreateRoleDelegationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => {
      const payload = {
        ...body,
        startDate: body.start,
        endDate: body.end,
      };
      delete payload.start;
      delete payload.end;
      delete payload.id;

      return api.post("/role-delegations", payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROLE_DELEGATIONS_KEY });
      qc.invalidateQueries({ queryKey: ["employees"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useDecideRoleDelegationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, approved, reason }) =>
      api.post(`/role-delegations/${id}/decide`, { approved, reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROLE_DELEGATIONS_KEY });
      qc.invalidateQueries({ queryKey: ["employees"] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useRevokeRoleDelegationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/role-delegations/${id}/revoke`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROLE_DELEGATIONS_KEY });
      qc.invalidateQueries({ queryKey: ["employees"] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useActivateRoleDelegationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/role-delegations/${id}/force-activate`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROLE_DELEGATIONS_KEY });
      qc.invalidateQueries({ queryKey: ["employees"] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}
